import {
  differenceInCalendarDays,
  format,
  isAfter,
  isBefore,
  isEqual,
  isMatch,
  parse,
  parseISO,
  startOfDay,
} from 'date-fns';

type TDate = Date | number | null | string;

export const REQUEST_DATE_FORMAT = 'yyyy-MM-dd';

const isDateOnly = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

export const isPastDate = (date: TDate): boolean => {
  if (date === null || date === undefined || date === '') {
    return false;
  }
  return isBefore(startOfDay(new Date(date)), startOfDay(new Date()));
};

export const isDateAfter = (date: TDate, dateToCompare: TDate) => {
  if (typeof date !== 'string' && date && typeof dateToCompare !== 'string' && dateToCompare) {
    return isAfter(date, dateToCompare);
  }
  return null;
};

export const isDateEqual = (date: TDate, dateToCompare: TDate) => {
  if (typeof date !== 'string' && date && typeof dateToCompare !== 'string' && dateToCompare) {
    return isEqual(date, dateToCompare);
  }
  return null;
};

// eslint-disable-next-line no-restricted-globals
export const isValidDate = (date: any) => !isNaN(Date.parse(date));

export function formatDate(date: string | null | undefined, dateFormat: string): string {
  if (date === null || date === undefined || date.trim() === '') {
    return '-';
  }
  const parseTimezone = date.match(/\[.+?]/g);
  const timezone = parseTimezone && parseTimezone.length ? parseTimezone[0] : '';

  if (!timezone) {
    return format(parseISO(date), dateFormat);
  }

  return format(parseISO(date.slice(0, -timezone.length)), dateFormat);
}

export const getFullYearsSince = (date: any): number => {
  if (!date) {
    return 0;
  }
  const now = new Date();
  const pastDate = new Date(
    date.replace(/\[.*\]$/, '').replace(/([+-]\d{2}:\d{2}):\d{2}/, '$1'),
  );
  let years = now.getFullYear() - pastDate.getFullYear();
  if (
    now.getMonth() < pastDate.getMonth() ||
    (now.getMonth() === pastDate.getMonth() && now.getDate() < pastDate.getDate())
  ) {
    years--;
  }
  return years;
};

export function leftDays(
  date: string | null | undefined,
  t: (key: string) => string,
  number: boolean = false,
): string {
  if (date === null || date === undefined || date.trim() === '') {
    return '';
  }
  const parseTimezone = date.match(/\[.+?]/g);
  const timezone = parseTimezone && parseTimezone.length ? parseTimezone[0] : '';
  const normalizedDateValue = timezone ? date.slice(0, -timezone.length) : date;
  const dateFormat = parseISO(normalizedDateValue);
  const todayStart = startOfDay(new Date());
  const expiryStart = startOfDay(dateFormat);
  const calendarDaysDifference = Math.abs(differenceInCalendarDays(expiryStart, todayStart));

  if (number) {
    return calendarDaysDifference.toString();
  }

  const label = calendarDaysDifference === 1 ? t('formatDate.day') : t('formatDate.days');
  return `${calendarDaysDifference} ${label}`;
}

export const getDateFromISO = (date: string | null) => {
  if (date === null || date === undefined || date.trim() === '') {
    return '';
  }
  const parseTimezone = date.match(/\[.+?]/g);

  if (parseTimezone === null) {
    return isDateOnly(date) ? parseISO(date) : new Date(date);
  }

  const timezone = parseTimezone && parseTimezone.length ? parseTimezone[0] : '';
  const dateFormat = parseISO(date.slice(0, -timezone.length));
  return dateFormat;
};

export const getDateFromISOOrNull = (date: string | null | Date) => {
  if (date instanceof Date) {
    return date;
  }
  if (date === null || date === undefined || date.trim() === '') {
    return null;
  }
  const parseTimezone = date.match(/\[.+?]/g);

  if (parseTimezone === null) {
    return isDateOnly(date) ? parseISO(date) : new Date(date);
  }

  const timezone = parseTimezone && parseTimezone.length ? parseTimezone[0] : '';
  const dateFormat = parseISO(date.slice(0, -timezone.length));
  return dateFormat;
};

export const getDateFromDateOrZonedIso = (date: string | null) => {
  if (date === null || date === undefined || date.trim() === '') {
    return '';
  }
  return parseISO(date.split('[')[0]);
};

export function simpleFormatDate(
  date: Date | string | null | undefined,
  dateFormat: string,
): string {
  if (typeof date === 'string') {
    date = new Date(getDateFromISO(date as string));
  }
  if (date === null || date === undefined) {
    return '-';
  }
  return format(date, dateFormat);
}

export function convertSimpleDate(
  dateStr: string | null | undefined,
  dateFormat: string,
): string {
  if (
    dateStr === null ||
    dateStr === undefined ||
    dateStr.trim() === '' ||
    !isMatch(dateStr, dateFormat)
  ) {
    return '';
  }
  const date = parse(dateStr, dateFormat, new Date()).toString();
  return date !== 'Invalid Date' ? date : '';
}

export function convertToSimpleDate(
  date: null | undefined | Date,
  dateFormat: string,
): string {
  if (date === null || date === undefined) {
    return '';
  }
  try {
    return format(date, dateFormat);
  } catch (e: any) {
    return '';
  }
}

export const formatDateTimeToIso = (
  dateTime: string | Date | number | undefined | null,
  endDay?: boolean,
) => {
  if (dateTime === undefined || dateTime === null || dateTime === '') {
    return '';
  }
  const dateTimeString = dateTime.toString();

  if (dateTimeString.includes('[')) {
    return dateTimeString;
  }

  const newDate = new Date(dateTimeString);
  if (endDay) {
    newDate.setHours(23, 59, 59);
  }
  return `${newDate.toISOString().slice(0, -5)}Z`;
};

export const formatDateToRequest = (
  inputDate: string | Date | undefined,
  withTime: boolean = false,
  withTZOffset = false,
): string => {
  if (inputDate === '' || inputDate === null || inputDate === undefined) {
    return '';
  }
  inputDate = inputDate.toString();

  if (!withTime) {
    // eslint-disable-next-line prefer-destructuring
    inputDate = inputDate.split(' ')[0];
  }

  const inputDateObj = new Date(inputDate);

  if (!withTime) {
    inputDateObj.setHours(12, 0, 0);
  }

  if (withTZOffset) {
    return format(inputDateObj, "yyyy-MM-dd'T'HH:mm:ssxxx");
  }

  return format(inputDateObj, "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

export const formatOnlyDateToRequest = (
  inputDate: string | Date | number | undefined | null,
): string => {
  if (inputDate === '' || inputDate === null || inputDate === undefined) {
    return '';
  }
  if (isDateOnly(inputDate)) {
    return format(parseISO(inputDate), REQUEST_DATE_FORMAT);
  }
  inputDate = inputDate.toString();
  // eslint-disable-next-line prefer-destructuring
  inputDate = inputDate.includes('[') ? inputDate.split('[')[0] : inputDate;
  const inputDateObj = new Date(inputDate);

  return format(inputDateObj, REQUEST_DATE_FORMAT);
};

export enum EAlertTime {
  SHORT = 7000,
  LONG = 12000,
}

export const clearAlertTimeout = (
  clearAlert: (type: string) => void,
  type: string,
  time: EAlertTime,
) => {
  const timeoutId = setTimeout(() => {
    clearAlert(type);
  }, time);

  return () => clearTimeout(timeoutId);
};

export const getDateDayName = (dateString: string | null | undefined) => {
  if (!dateString) return '-';
  const cleanedDate = dateString.replace(/\[.*\]/, '');
  const date = new Date(cleanedDate);

  return date.toLocaleDateString('en-US', { weekday: 'short' });
};

export const areDatesEqual = (
  date1: Date | null | undefined | string,
  date2: Date | null | undefined | string,
  stripHours = false,
): boolean => {
  if (!date1 && !date2) return true;
  if (!date1 || !date2) return false;

  const dateObj1 = new Date(date1);
  const dateObj2 = new Date(date2);

  if (stripHours) {
    dateObj1.setHours(0, 0, 0, 0);
    dateObj2.setHours(0, 0, 0, 0);
  }

  return dateObj1.getTime() === dateObj2.getTime();
};
